import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Layers, Plus, Save, X } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api } from "../../lib/api";

const BLANK = {
  slug: "", name: "", description: "", delivery: "slot_series",
  default_members_only: true, badge_label: "", sort_order: 100, active: true,
  credit_allowance: "", credit_period: "calendar_month", credit_period_days: "",
};

// A program is the recurring container (Capital Series, Founder Labs, Build from
// the Dock, …) that owns identity and, later, the per-member booking allowance.
export default function AdminPrograms() {
  const [programs, setPrograms] = useState(null);
  const [editing, setEditing] = useState(null); // a program object, or BLANK for new

  const load = () => api.get("/admin/programs").then((r) => setPrograms(r.data)).catch(() => toast.error("Couldn't load programs"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Name is required");
    // Normalise the numeric/nullable fields the API expects.
    const payload = {
      ...editing,
      credit_allowance: editing.credit_allowance === "" || editing.credit_allowance == null ? null : parseInt(editing.credit_allowance),
      credit_period_days: editing.credit_period_days === "" || editing.credit_period_days == null ? null : parseInt(editing.credit_period_days),
      sort_order: parseInt(editing.sort_order) || 100,
    };
    try {
      if (editing.id) {
        const { id, slug, created_at, ...patch } = payload;
        await api.patch(`/admin/programs/${editing.id}`, patch);
      } else {
        if (!payload.slug?.trim()) return toast.error("Slug is required");
        await api.post("/admin/programs", payload);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <AdminShell
      testId="admin-programs"
      eyebrow="Programs"
      title="Recurring programs"
      actions={!editing && (
        <button onClick={() => setEditing({ ...BLANK })} className="btn-primary" data-testid="program-new">
          <Plus size={13} /> New program
        </button>
      )}
    >
        <div className="max-w-3xl">

          {editing ? (
            <ProgramForm value={editing} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} />
          ) : programs == null ? (
            <div className="skeleton h-40" />
          ) : programs.length === 0 ? (
            <div className="card-hair p-8 text-center text-sm text-muted-foreground">No programs yet.</div>
          ) : (
            <div className="space-y-2" data-testid="program-list">
              {programs.map((p) => (
                <button key={p.id} onClick={() => setEditing({ ...BLANK, ...p, credit_allowance: p.credit_allowance ?? "", credit_period_days: p.credit_period_days ?? "" })}
                  className="w-full text-left card-hair p-4 flex items-center justify-between hover:bg-white/40 transition" data-testid={`program-${p.slug}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-lg">{p.name}</span>
                      {!p.active && <span className="chip chip-cream-2 text-[10px]">Inactive</span>}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-0.5">
                      {p.slug} · {p.delivery === "slot_series" ? "slot series" : "event"}
                      {p.credit_allowance != null && ` · ${p.credit_allowance}/${p.credit_period === "calendar_month" ? "mo" : p.credit_period_days + "d"}`}
                    </div>
                  </div>
                  {p.badge_label && <span className="chip chip-purple text-[10px]">{p.badge_label}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
    </AdminShell>
  );
}

function ProgramForm({ value: v, onChange, onSave, onCancel }) {
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="card-hair p-6 space-y-5" data-testid="program-form">
      <div className="flex items-center justify-between">
        <Overline>{v.id ? "Edit program" : "New program"}</Overline>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" data-testid="program-cancel"><X size={16} /></button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Name">
          <input className="input-field" value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="Founder Labs" data-testid="program-name" />
        </Field>
        <Field label="Slug">
          <input className="input-field font-mono" value={v.slug} disabled={!!v.id} onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="founder-labs" data-testid="program-slug" />
        </Field>
      </div>

      <Field label="Description">
        <input className="input-field" value={v.description || ""} onChange={(e) => set({ description: e.target.value })} data-testid="program-description" />
      </Field>

      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Delivery">
          <select className="input-field" value={v.delivery} onChange={(e) => set({ delivery: e.target.value })} data-testid="program-delivery">
            <option value="slot_series">Slot series</option>
            <option value="event">Event</option>
          </select>
        </Field>
        <Field label="Badge label">
          <input className="input-field" value={v.badge_label || ""} onChange={(e) => set({ badge_label: e.target.value })} placeholder="Founder Labs" data-testid="program-badge" />
        </Field>
        <Field label="Sort order">
          <input type="number" className="input-field" value={v.sort_order} onChange={(e) => set({ sort_order: e.target.value })} data-testid="program-sort" />
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Credit allowance">
          <input type="number" min={1} className="input-field" value={v.credit_allowance} onChange={(e) => set({ credit_allowance: e.target.value })} placeholder="none" data-testid="program-allowance" />
          <div className="text-[11px] text-muted-foreground mt-1">Blank = no booking limit.</div>
        </Field>
        <Field label="Credit period">
          <select className="input-field" value={v.credit_period} onChange={(e) => set({ credit_period: e.target.value })} data-testid="program-period">
            <option value="calendar_month">Calendar month</option>
            <option value="rolling_days">Rolling days</option>
          </select>
        </Field>
        {v.credit_period === "rolling_days" && (
          <Field label="Period days">
            <input type="number" min={1} className="input-field" value={v.credit_period_days} onChange={(e) => set({ credit_period_days: e.target.value })} data-testid="program-period-days" />
          </Field>
        )}
      </div>

      <div className="flex items-center gap-6 pt-1">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={v.default_members_only} onChange={(e) => set({ default_members_only: e.target.checked })} className="accent-primary" data-testid="program-members-only" />
          Members-only by default
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={v.active} onChange={(e) => set({ active: e.target.checked })} className="accent-primary" data-testid="program-active" />
          Active
        </label>
      </div>

      <div className="flex justify-end pt-2">
        <button onClick={onSave} className="btn-primary" data-testid="program-save"><Save size={13} /> Save program</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-muted-foreground mb-2">{label}</div>
      {children}
    </label>
  );
}
